<div class="sav-site-container">
  <app-breadcrumb [items]="crumbs()" />
</div>

<section class="sav-store-banner" aria-label="Store banner">
  <div class="sav-store-banner-container">
    <div class="sav-store-banner-surface" [class.is-logo-only]="!bannerImageUrl()">
      <!-- Banner is optional; without one the hero is just the logo strip. -->
      @if (bannerImageUrl()) {
        <img
          [src]="bannerImageUrl()"
          [alt]="bannerAltText()"
          class="sav-store-banner-image"
          width="1500"
          height="300"
        />
      }

      <div class="sav-store-banner-overlay">
        @if (logoImageUrl()) {
          <img
            [src]="logoImageUrl()"
            [alt]="storeName() + ' logo'"
            class="sav-store-banner-logo"
            width="150"
            height="150"
          />
        }

        <div class="sav-store-banner-actions">
          <!--
            Goes to the affiliate link on the Store record (the same one the coupon pages use), so
            this page never holds a second copy that could drift. Rendered as a plain span until the
            store loads, because a link with no href is worse than no link.
          -->
          @if (affiliateLink()) {
            <a
              class="sav-store-banner-button sav-store-banner-button-secondary"
              [href]="affiliateLink()"
              target="_blank"
              rel="nofollow sponsored noopener"
            >
              <span>{{ visitButtonLabel() }}</span>
            </a>
          } @else {
            <span class="sav-store-banner-button sav-store-banner-button-secondary">
              <span>{{ visitButtonLabel() }}</span>
            </span>
          }
          <!--
            Follow and Share, with this store's real figures — the same ones its coupon page shows,
            because the API computes both from the same store record. They used to be the literals
            "24.6K" and "3.2K": identical on every store on the site, and contradicting the coupon
            page for all of them.

            Follow links to the coupon page rather than acting here. Following is an email signup and
            that form lives there, so a one-click "Follow" on this page would be a button that
            promises something it cannot do. Same reasoning as the featured hero on /collection.
          -->
          @if (couponPagePath()) {
            <a
              class="sav-store-banner-button sav-store-banner-button-primary"
              [routerLink]="couponPagePath()"
            >
              <span>Follow</span>
              <span class="sav-store-banner-count">{{ formatCount(followerCount()) }}</span>
            </a>
          }
          <button
            type="button"
            class="sav-store-banner-button sav-store-banner-button-secondary"
            (click)="shareStore()"
          >
            <span>{{ shareLabel() }}</span>
            <span class="sav-store-banner-count">{{ formatCount(shareCount()) }}</span>
          </button>
        </div>
      </div>
    </div>

    <!--
      The page's H1. It had none at all: the store's identity was carried only by the banner logo,
      an <img>, so the outline opened on the first section's h2 and no heading named the page's
      subject. Text matches what the banner already says.
    -->
    <h1 class="sav-store-banner-title">{{ storeName() }} Deals &amp; Product Offers</h1>

    <!-- Cross-link to the coupon side of this store. -->
    @if (couponPagePath()) {
      <a class="sav-cross-page-link" [routerLink]="couponPagePath()">
        View {{ storeName() }} All Coupons and Offers
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      </a>
    }
  </div>
</section>

<section
  #storeNav
  class="sav-store-nav"
  aria-label="Store categories and search"
  [style.top.px]="stickyNavTop()"
>
  <div class="sav-store-nav-container">
    <div class="sav-store-nav-scroll">
      <nav class="sav-store-nav-links">
        @for (tab of navTabs(); track tab.key) {
          <button type="button" class="sav-store-nav-link" (click)="scrollToNavSection(tab.key)">
            {{ tab.label }}
          </button>
        }
      </nav>

      <div class="sav-store-nav-search-wrap">
        <button
          type="button"
          class="sav-store-nav-search-trigger"
          (click)="toggleSearch()"
          [attr.aria-expanded]="isSearchOpen()"
          [attr.aria-label]="isSearchOpen() ? 'Close search' : 'Open search'"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              d="M18.031 16.6168L22.3137 20.8995L20.8995 22.3137L16.6168 18.031C15.0769 19.263 13.124 20 11 20C6.032 20 2 15.968 2 11C2 6.032 6.032 2 11 2C15.968 2 20 6.032 20 11C20 13.124 19.263 15.0769 18.031 16.6168ZM16.0247 15.8748C17.2475 14.6146 18 12.8956 18 11C18 7.1325 14.8675 4 11 4C7.1325 4 4 7.1325 4 11C4 14.8675 7.1325 18 11 18C12.8956 18 14.6146 17.2475 15.8748 16.0247L16.0247 15.8748Z"
            />
          </svg>
        </button>

        @if (isSearchOpen()) {
          <button
            type="button"
            class="sav-store-nav-search-backdrop"
            (click)="closeSearch()"
            aria-label="Close search"
          ></button>
        }

        <form class="sav-store-nav-search-popup" [class.is-open]="isSearchOpen()" role="search">
          <div class="sav-store-nav-search">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                d="M18.031 16.6168L22.3137 20.8995L20.8995 22.3137L16.6168 18.031C15.0769 19.263 13.124 20 11 20C6.032 20 2 15.968 2 11C2 6.032 6.032 2 11 2C15.968 2 20 6.032 20 11C20 13.124 19.263 15.0769 18.031 16.6168ZM16.0247 15.8748C17.2475 14.6146 18 12.8956 18 11C18 7.1325 14.8675 4 11 4C7.1325 4 4 7.1325 4 11C4 14.8675 7.1325 18 11 18C12.8956 18 14.6146 17.2475 15.8748 16.0247L16.0247 15.8748Z"
              />
            </svg>
            <input
              #storeSearchInput
              type="search"
              [placeholder]="'Search all ' + storeName()"
              [value]="searchTerm()"
              (input)="onSearchInput($event)"
            />
          </div>
        </form>
      </div>
    </div>
  </div>
</section>

<!-- Editorial lookbook (admin-managed). Whole image is a link to its Shop Now target. -->
@if (editorial(); as ed) {
  @if (ed.full) {
    <section class="sav-store-feature" aria-label="Featured collection">
      <div class="sav-site-container">
        @if (ed.fullHeading) {
          <header class="sav-section-head">
            <h2>{{ ed.fullHeading }}</h2>
          </header>
        }
        <a
          class="sav-store-feature-surface sav-editorial-link"
          [href]="ed.full.url"
          target="_blank"
          rel="nofollow sponsored noopener"
        >
          <img
            [src]="ed.full.imageUrl"
            [alt]="storeName() + ' collection'"
            class="sav-store-feature-image"
          />
          <span class="sav-editorial-shop">Shop Now</span>
        </a>
      </div>
    </section>
  }
}

@if (isSearchActive()) {
  <!-- Search results replace the whole section stack while a term is active. -->
  <section
    class="sav-essentials"
    aria-label="Search results"
    [style.scroll-margin-top.px]="sectionScrollMargin()"
  >
    <div class="sav-site-container">
      <header class="sav-essentials-head">
        <div>
          <h2>Results for “{{ searchTerm() }}”</h2>
          <p>Matches from the {{ storeName() }} catalogue</p>
        </div>
        @if (searchTotal() > 0) {
          <p class="sav-store-products-count">{{ searchTotal() }} found</p>
        }
      </header>

      @if (isSearching() && searchResults().length === 0) {
        <p class="sav-store-products-note">Searching…</p>
      } @else if (searchResults().length === 0) {
        <p class="sav-store-products-note">No products match “{{ searchTerm() }}”.</p>
      } @else {
        <div class="sav-product-grid">
          @for (product of searchResults(); track product.id) {
            <app-product-card
              [title]="product.name"
              [image]="productImage(product)"
              [hoverImage]="productHoverImage(product)"
              [storeName]="storeName()"
              [storeLogoUrl]="logoImageUrl()"
              [price]="productPrice(product)"
              [oldPrice]="productOldPrice(product)"
              [external]="productIsExternal(product)"
              [detailHref]="productDetailHref(product)"
              [productId]="product.id"
            />
          }
        </div>
      }
    </div>
  </section>
} @else {
  @for (section of sections(); track section.id) {
    <!--
    One block per product section: the discounted strip, new arrivals, then a block per navigation
    tab. A section with nothing in it is dropped entirely — a heading over an empty grid reads as a
    fault, and stores with no discounted stock genuinely have nothing to show here.
  -->
    @if (section.loading || section.products.length > 0 || (!section.eager && !section.loaded)) {
      <section
        class="sav-essentials"
        [id]="'deals-section-' + section.id"
        [attr.aria-label]="section.heading"
        [style.scroll-margin-top.px]="sectionScrollMargin()"
        [attr.data-lazy-section]="!section.eager && !section.loaded ? section.id : null"
      >
        <div class="sav-site-container">
          <header class="sav-essentials-head">
            <div>
              <h2>{{ section.heading }}</h2>
              <p>{{ storeName() }} products in this collection</p>
            </div>

            @if (section.total > 0) {
              <p class="sav-store-products-count">
                Showing {{ section.products.length }} of {{ section.total }}
              </p>
            }
          </header>

          @if (section.products.length === 0) {
            <!-- Loading, or a lazy section waiting for the observer: keep height so scroll + the
               IntersectionObserver have something to target. -->
            <div class="sav-store-section-placeholder">
              @if (section.loading) {
                <p class="sav-store-products-note">Loading products…</p>
              }
            </div>
          } @else {
            <div class="sav-product-grid">
              @for (product of section.products; track product.id) {
                <app-product-card
                  [title]="product.name"
                  [image]="productImage(product)"
                  [hoverImage]="productHoverImage(product)"
                  [storeName]="storeName()"
                  [storeLogoUrl]="logoImageUrl()"
                  [price]="productPrice(product)"
                  [oldPrice]="productOldPrice(product)"
                  [external]="productIsExternal(product)"
                  [detailHref]="productDetailHref(product)"
                  [productId]="product.id"
                />
              }
            </div>

            @if (hasMore(section)) {
              <div class="sav-store-load-more-row">
                <button
                  type="button"
                  class="sav-store-load-more"
                  (click)="loadMore(section)"
                  [disabled]="section.loading"
                >
                  {{ section.loading ? 'Loading…' : 'Load More' }}
                </button>
              </div>
            }
          }
        </div>
      </section>
    }

    <!--
    Editorial collage (half-width hero + four-tile collage): rendered under the discounted section
    when the store has one, otherwise under New Arrivals (see collageAfterSectionId).
  -->
    @if (section.id === collageAfterSectionId()) {
      @if (editorial(); as ed) {
        @if (ed.half || ed.collage.length) {
          <section class="sav-collection-grid" aria-label="Shop collections">
            <div class="sav-site-container">
              @if (ed.collageHeading) {
                <header class="sav-section-head">
                  <h2>{{ ed.collageHeading }}</h2>
                </header>
              }
            </div>
            <div class="sav-site-container sav-collection-grid-layout">
              @if (ed.half) {
                <a
                  class="sav-collection-grid-hero sav-editorial-link"
                  [href]="ed.half.url"
                  target="_blank"
                  rel="nofollow sponsored noopener"
                >
                  <img
                    [src]="ed.half.imageUrl"
                    [alt]="storeName() + ' collection'"
                    class="sav-collection-grid-image"
                  />
                  <span class="sav-editorial-shop">Shop Now</span>
                </a>
              }

              @if (ed.collage.length) {
                <div class="sav-collection-grid-stack">
                  @for (tile of ed.collage; track tile.imageUrl) {
                    <a
                      class="sav-collection-grid-tile sav-editorial-link"
                      [href]="tile.url"
                      target="_blank"
                      rel="nofollow sponsored noopener"
                    >
                      <img
                        [src]="tile.imageUrl"
                        [alt]="storeName() + ' collection'"
                        class="sav-collection-grid-image"
                      />
                      <span class="sav-editorial-shop">Shop Now</span>
                    </a>
                  }
                </div>
              }
            </div>
          </section>
        }
      }
    }

    <!--
    Bottom-section image groups distributed under the nav-tab product sections: the full-width banner
    under the 1st nav tab, the two-up under the 2nd, the three-tile under the 3rd. Breaking the page
    up this way reads better than stacking all the imagery at the foot.
  -->
    @if (bottom(); as bt) {
      @if (bt.full && section.id === navTabSectionId(0)) {
        <section class="sav-store-feature sav-store-feature-lace" aria-label="Featured collection">
          <div class="sav-site-container">
            @if (bt.fullHeading) {
              <header class="sav-section-head">
                <h2>{{ bt.fullHeading }}</h2>
              </header>
            }
            <a
              class="sav-store-feature-surface sav-editorial-link"
              [href]="bt.full.url"
              target="_blank"
              rel="nofollow sponsored noopener"
            >
              <img
                [src]="bt.full.imageUrl"
                [alt]="storeName() + ' collection'"
                class="sav-store-feature-image"
              />
              <span class="sav-editorial-shop">Shop Now</span>
            </a>
          </div>
        </section>
      }

      @if (bt.duo.length && section.id === navTabSectionId(1)) {
        <section class="sav-editorial-duo" aria-label="Collections">
          <div class="sav-site-container">
            @if (bt.duoHeading) {
              <header class="sav-section-head">
                <h2>{{ bt.duoHeading }}</h2>
              </header>
            }
            <div class="sav-editorial-duo-grid">
              @for (item of bt.duo; track item.imageUrl) {
                <a
                  class="sav-editorial-duo-feature sav-editorial-link"
                  [href]="item.url"
                  target="_blank"
                  rel="nofollow sponsored noopener"
                >
                  <img
                    [src]="item.imageUrl"
                    [alt]="storeName() + ' collection'"
                    class="sav-editorial-duo-image"
                  />
                  <span class="sav-editorial-shop">Shop Now</span>
                </a>
              }
            </div>
          </div>
        </section>
      }

      @if (bt.trio.length && section.id === navTabSectionId(2)) {
        <section class="sav-category-editorial" aria-label="Shop by category">
          <div class="sav-site-container">
            @if (bt.trioHeading) {
              <header class="sav-section-head">
                <h2>{{ bt.trioHeading }}</h2>
              </header>
            }
            <div class="sav-category-editorial-grid">
              @for (item of bt.trio; track item.imageUrl) {
                <a
                  class="sav-category-editorial-tile sav-editorial-link"
                  [href]="item.url"
                  target="_blank"
                  rel="nofollow sponsored noopener"
                >
                  <img
                    [src]="item.imageUrl"
                    [alt]="storeName() + ' collection'"
                    class="sav-category-editorial-image"
                  />
                  <span class="sav-editorial-shop">Shop Now</span>
                </a>
              }
            </div>
          </div>
        </section>
      }
    }
  }
}

@if (relatedStores().length > 0) {
  <!-- Related-category stores that also have a deals page. Circular logo carousel above the video. -->
  <section class="py-10" aria-label="Related stores">
    <div class="sav-site-container">
      <header class="sav-section-head">
        <h2>Related Stores</h2>
        <p>More stores you might like</p>
      </header>

      <div class="relative mt-8">
        <button
          type="button"
          class="absolute top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-orange bg-white/95 text-orange shadow-soft transition hover:bg-orange hover:text-white left-0"
          aria-label="Previous stores"
          (click)="scrollRelated(-1)"
        >
          <svg
            class="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>

        <div
          #relatedTrack
          class="sav-scrollable flex gap-7 overflow-x-auto scroll-smooth px-12 py-2 xl:gap-10"
        >
          @for (store of relatedStores(); track store.path) {
            <a
              [routerLink]="store.path"
              [attr.aria-label]="store.name"
              class="group grid h-[120px] w-[120px] shrink-0 place-items-center overflow-hidden rounded-full bg-white shadow-[0_6px_16px_rgba(16,26,40,0.12)] xl:h-[138px] xl:w-[138px]"
            >
              <!-- The link's aria-label names the store. A store without a logo used to render a
                   broken <img src="">; the component paints its name on white instead. -->
              <app-store-logo
                class="h-full w-full p-2 transition duration-300 group-hover:scale-110"
                [logoUrl]="store.logoUrl"
                [name]="store.name"
                decorative
              />
            </a>
          }
        </div>

        <button
          type="button"
          class="absolute top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-orange bg-white/95 text-orange shadow-soft transition hover:bg-orange hover:text-white right-0"
          aria-label="Next stores"
          (click)="scrollRelated(1)"
        >
          <svg
            class="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
  </section>
}

@if (video(); as vid) {
  <section class="sav-campaign-video" aria-label="Campaign video">
    <div class="sav-site-container">
      @if (vid.heading) {
        <header class="sav-section-head">
          <h2>{{ vid.heading }}</h2>
        </header>
      }
      <div class="sav-campaign-video-surface">
        @if (isCampaignVideoPlaying()) {
          <!-- Loaded only after the play click, so YouTube is not fetched on page view. -->
          <iframe
            class="sav-campaign-video-media"
            [src]="videoEmbedUrl()"
            title="Campaign video"
            frameborder="0"
            allow="
              accelerometer;
              autoplay;
              clipboard-write;
              encrypted-media;
              gyroscope;
              picture-in-picture;
              web-share;
            "
            allowfullscreen
          ></iframe>
        } @else {
          <img
            [src]="vid.thumbnailUrl"
            alt="Campaign video"
            class="sav-campaign-video-media sav-campaign-video-poster"
          />
          <button
            type="button"
            class="sav-campaign-video-play"
            aria-label="Play campaign video"
            (click)="playCampaignVideo()"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M8 6.82v10.36c0 .79.87 1.27 1.54.84l8.14-5.18a1 1 0 0 0 0-1.68L9.54 5.98A1 1 0 0 0 8 6.82Z"
              />
            </svg>
          </button>
        }
      </div>
    </div>
  </section>
}
